import { ANALYTICS_CONFIG } from './config.js';
import { logger, validateAnalyticsParams, normalizeProductData, normalizeCategoryData, normalizeSellerData, createItemsArray, debounce, throttle } from './utils.js';
import googleAnalyticsService from './GoogleAnalyticsService.js';

class EventTracker {
  constructor() {
    // Debounce per eventi frequenti come scroll e resize
    this.debouncedTrack = debounce(this._trackEvent.bind(this), 300);
    
    // Throttle per eventi come click
    this.throttledTrack = throttle(this._trackEvent.bind(this), 100);
  }

  // Metodo base per tracking eventi
  _trackEvent(eventName, parameters = {}) {
    try {
      // Validazione parametri
      const validation = validateAnalyticsParams(parameters);
      if (!validation.isValid) {
        logger.error('Invalid analytics parameters', validation.errors);
        return;
      }

      // Invio evento tramite GA service
      googleAnalyticsService.sendEvent(eventName, parameters);
    } catch (error) {
      logger.error(`Failed to track event: ${eventName}`, error);
    }
  }

  // === EVENTI PRODOTTO ===
  
  // Visualizzazione prodotto
  trackProductView(productData) {
    const normalizedProduct = normalizeProductData(productData);
    if (!normalizedProduct) return;

    this._trackEvent(ANALYTICS_CONFIG.EVENTS.VIEW_ITEM, {
      currency: normalizedProduct.currency,
      value: normalizedProduct.price,
      items: [normalizedProduct]
    });
  }

  // Click su prodotto
  trackProductClick(productData, position = null) {
    const normalizedProduct = normalizeProductData(productData);
    if (!normalizedProduct) return;

    this._trackEvent(ANALYTICS_CONFIG.EVENTS.SELECT_ITEM, {
      currency: normalizedProduct.currency,
      value: normalizedProduct.price,
      items: [{
        ...normalizedProduct,
        item_list_name: ANALYTICS_CONFIG.PARAMETERS.ITEM_LIST_NAME,
        index: position
      }]
    });
  }

  // Click verso sito esterno
  trackExternalClick(productData, sellerData) {
    const normalizedProduct = normalizeProductData(productData);
    const normalizedSeller = normalizeSellerData(sellerData);
    
    if (!normalizedProduct || !normalizedSeller) return;

    this._trackEvent(ANALYTICS_CONFIG.EVENTS.CLICK_EXTERNAL_LINK, {
      currency: normalizedProduct.currency,
      value: normalizedProduct.price,
      items: [{
        ...normalizedProduct,
        seller_id: normalizedSeller.seller_id,
        seller_name: normalizedSeller.seller_name
      }]
    });
  }

  // === EVENTI WISHLIST ===
  
  // Aggiunta alla wishlist
  trackWishlistAdd(productData) {
    const normalizedProduct = normalizeProductData(productData);
    if (!normalizedProduct) return;

    this._trackEvent(ANALYTICS_CONFIG.EVENTS.ADD_TO_WISHLIST, {
      currency: normalizedProduct.currency,
      value: normalizedProduct.price,
      items: [normalizedProduct]
    });
  }

  // Rimozione dalla wishlist
  trackWishlistRemove(productData) {
    const normalizedProduct = normalizeProductData(productData);
    if (!normalizedProduct) return;

    this._trackEvent(ANALYTICS_CONFIG.EVENTS.REMOVE_FROM_WISHLIST, {
      currency: normalizedProduct.currency,
      value: normalizedProduct.price,
      items: [normalizedProduct]
    });
  }

  // === EVENTI RICERCA E FILTRI ===
  
  // Ricerca
  trackSearch(searchTerm, resultsCount = 0) {
    if (!searchTerm || typeof searchTerm !== 'string') {
      logger.warn('Invalid search term provided');
      return;
    }

    this._trackEvent(ANALYTICS_CONFIG.EVENTS.SEARCH, {
      search_term: searchTerm,
      results_count: parseInt(resultsCount) || 0
    });
  }

  // Applicazione filtri
  trackFilter(filterType, filterValue) {
    if (!filterType || !filterValue) {
      logger.warn('Invalid filter parameters provided');
      return;
    }

    this._trackEvent(ANALYTICS_CONFIG.EVENTS.APPLY_FILTER, {
      filter_type: filterType,
      filter_value: filterValue
    });
  }

  // === EVENTI CATEGORIA ===
  
  // Visualizzazione categoria
  trackCategoryView(categoryData, page = 1, items = []) {
    const normalizedCategory = normalizeCategoryData(categoryData);
    if (!normalizedCategory) return;

    const normalizedItems = createItemsArray(items);

    this._trackEvent(ANALYTICS_CONFIG.EVENTS.VIEW_ITEM_LIST, {
      item_list_name: normalizedCategory.item_list_name,
      item_list_id: normalizedCategory.item_category_id,
      page_number: parseInt(page) || 1,
      items: normalizedItems.length > 0 ? normalizedItems : [normalizedCategory]
    });
  }

  // === EVENTI SELLER ===
  
  // Visualizzazione seller
  trackSellerView(sellerData) {
    const normalizedSeller = normalizeSellerData(sellerData);
    if (!normalizedSeller) return;

    this._trackEvent(ANALYTICS_CONFIG.EVENTS.VIEW_SELLER, {
      seller_id: normalizedSeller.seller_id,
      seller_name: normalizedSeller.seller_name,
      seller_category: normalizedSeller.seller_category
    });
  }

  // === EVENTI AUTHENTICATION ===
  
  // Eventi di autenticazione
  trackAuth(action, method, success = true, userType = null, error = null) {
    const validActions = ['login', 'signup', 'logout', 'password_reset'];
    const validMethods = ['email_password', 'social', 'magic_link'];

    if (!validActions.includes(action)) {
      logger.warn(`Invalid auth action: ${action}`);
      return;
    }

    this._trackEvent(action, {
      method: validMethods.includes(method) ? method : 'unknown',
      success: Boolean(success),
      user_type: userType,
      error: error || null
    });
  }

  // === EVENTI NAVIGAZIONE ===
  
  // Navigazione tra pagine
  trackNavigation(from, to, elementType = 'link') {
    if (!from || !to) {
      logger.warn('Invalid navigation parameters provided');
      return;
    }

    this._trackEvent(ANALYTICS_CONFIG.EVENTS.NAVIGATION, {
      from_page: from,
      to_page: to,
      element_type: elementType
    });
  }

  // === EVENTI CUSTOM ===
  
  // Evento personalizzato generico
  trackCustomEvent(eventName, parameters = {}) {
    if (!eventName || typeof eventName !== 'string') {
      logger.warn('Invalid custom event name provided');
      return;
    }

    this._trackEvent(eventName, parameters);
  }

  // === EVENTI CON DEBOUNCE/THROTTLE ===
  
  // Eventi con debounce (per scroll, resize, etc.)
  trackDebounced(eventName, parameters = {}) {
    this.debouncedTrack(eventName, parameters);
  }

  // Eventi con throttle (per click frequenti)
  trackThrottled(eventName, parameters = {}) {
    this.throttledTrack(eventName, parameters);
  }

  // === UTILITY METHODS ===
  
  // Tracking batch di eventi
  trackBatch(events = []) {
    if (!Array.isArray(events)) {
      logger.warn('Invalid events array provided');
      return;
    }

    events.forEach(event => {
      if (event && event.name && event.parameters) {
        this._trackEvent(event.name, event.parameters);
      }
    });
  }

  // Controllo disponibilità
  isAvailable() {
    return googleAnalyticsService.getStatus().isAvailable;
  }

  // Reset debounce/throttle
  reset() {
    // Reset dei timer per debounce/throttle
    this.debouncedTrack = debounce(this._trackEvent.bind(this), 300);
    this.throttledTrack = throttle(this._trackEvent.bind(this), 100);
  }
}

// Singleton instance
const eventTracker = new EventTracker();

export default eventTracker; 